/**
 * D6 -- the incident, as the person accountable for the service needs
 * it.
 *
 * The owner's alert landed him on an operator's finding page: a raw
 * external reference, a severity chip, a payload viewer, and "Linked
 * CAPs require framework access". Nothing on it answered the two
 * questions he actually has -- how long has this been going on, and
 * when does it become a breach -- though the product knows both.
 *
 * Rendered only when the finding sits on something he owns. For the
 * operator the page is already right; this would be noise.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { Finding } from "shared/api/types";
import { useMyEstate } from "twin/home/useMyEstate";
import { useSeatHolders } from "shared/api/useSeatHolders";
import { useToleranceState } from "shared/tolerance/useToleranceState";
import { asSentence, toleranceSentence } from "shared/tolerance/toleranceSentence";
import { formatTime } from "shared/time/formatDate";
import { roleLabel } from "shared/vocab/workflowState";
import { useFindingLinkedCAPs } from "twin/finding/useFindingLinkedCAPs";

/** The source the impact-tolerance monitor stamps on the findings it
 *  raises (mirrors `TOLERANCE_AUTO_SOURCE` in twin/tolerance.py). */
const TOLERANCE_AUTO_SOURCE = "tolerance.auto";

function numberFrom(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function stringFrom(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/** "1h 12m" -- how long this has been going on, in the words a person
 *  would use. Not a timestamp difference in seconds. */
export function elapsedSince(from: string, now: Date): string {
  const start = new Date(from).getTime();
  if (Number.isNaN(start)) return "";
  const mins = Math.max(0, Math.floor((now.getTime() - start) / 60000));
  const h = Math.floor(mins / 60);
  return h > 0 ? `${h}h ${mins % 60}m` : `${mins}m`;
}

/** When the tolerance runs out, or null when there is none to run out. */
export function breachAt(raisedAt: string, toleranceHours: number | null): Date | null {
  if (toleranceHours === null) return null;
  const start = new Date(raisedAt).getTime();
  if (Number.isNaN(start)) return null;
  return new Date(start + toleranceHours * 3_600_000);
}

/** ROUND 13 G3 -- whether this strip will render, so the page can put
 *  the comment box inside it rather than a screen and a half further
 *  down. One predicate, used by both, because two copies of "is this
 *  his incident" is how the two disagree. */
export function ownerStripApplies(
  finding: Pick<Finding, "entity_id" | "status">,
  ibses: { id: string }[] | undefined,
): boolean {
  return (
    finding.status === "open" && (ibses ?? []).some((i) => i.id === finding.entity_id)
  );
}

export function OwnerIncidentStrip({
  finding,
  commentSlot,
  mayReadPlans = true,
}: {
  finding: Finding;
  /** G3 -- the one control this page asks him for, on the first
   *  screen. Measured at 1.34 screens below the fold on a phone,
   *  further than round 12 measured it. */
  commentSlot?: React.ReactNode;
  /** Round 18 G28 -- whether this seat may read the linked plans. */
  mayReadPlans?: boolean;
}) {
  const { t, i18n } = useTranslation();
  // Round 18 G23 -- a pending leg names its holder.
  const seats = useSeatHolders();
  const estate = useMyEstate();
  const tolerance = useToleranceState();
  // Round 18 G28 -- a seat without the Framework module is not asked
  // for plans it cannot read; the owner's finding was refused 403.
  const caps = useFindingLinkedCAPs(finding.id, mayReadPlans);

  const owned = (estate.data?.ibses ?? []).find((i) => i.id === finding.entity_id);
  // Only for the owner, only while it is still happening. A closed
  // incident wants the record, not a running clock.
  if (!owned || finding.status !== "open") return null;

  const state = (tolerance.data?.rows ?? []).find(
    (r) => r.ibs_service_id === finding.entity_id,
  );
  // The number IN FORCE, which is what a breach is judged against. A
  // proposal awaiting signature is shown as what it is, never as the
  // figure driving the clock.
  const hours = state?.hours ?? null;
  const breach = breachAt(finding.raised_at, hours);
  const now = new Date();

  /* ROUND 13 G2 -- the tolerance monitor's own finding is not an
     ordinary incident with a clock running towards a breach. It exists
     BECAUSE the live projection already exceeds the declared
     tolerance, so a countdown to a breach two hours from now sat above
     a title saying the breach had happened, and the record's own
     sentence -- the projection, the tolerance and what drove it --
     reached none of the owner's screens.

     Keyed on the source system rather than the payload, because the
     source is what the monitor stamps and what the exclusion in the
     war room already matches on. The payload supplies the figures. */
  const isToleranceBreach = finding.source_system_code === TOLERANCE_AUTO_SOURCE;
  const projected = numberFrom(finding.payload?.["projected_recovery_hours"]);
  const declaredInPayload = numberFrom(finding.payload?.["impact_tolerance_hours"]);
  const via = stringFrom(finding.payload?.["via"]);
  const showProjection = isToleranceBreach && projected !== null;

  return (
    <section
      data-testid="owner-incident-strip"
      className="rounded-lg border border-rag-redEdge bg-rag-redBg p-4"
    >
      <p data-testid="owner-incident-headline" className="text-base text-slate-900">
        {t("modules.twin.finding.owner.headline", {
          name: owned.name,
          severity: finding.severity,
          since: formatTime(finding.raised_at, i18n.language),
          elapsed: elapsedSince(finding.raised_at, now),
        })}
      </p>
      {showProjection && (
        <div
          data-testid="owner-incident-projection"
          className="mt-1 text-sm text-slate-800"
        >
          <p>
            {t("modules.twin.finding.owner.toleranceBreached", {
              projected,
              tolerance: declaredInPayload ?? hours,
            })}
          </p>
          {via && (
            <p className="mt-0.5 text-slate-700">
              {t("modules.twin.finding.owner.toleranceBreachedVia", { via })}
            </p>
          )}
          <p className="mt-0.5 text-xs text-slate-600">
            {t("modules.twin.finding.owner.toleranceBreachedWhen", {
              when: formatTime(finding.raised_at, i18n.language),
            })}
          </p>
        </div>
      )}
      <p
        data-testid="owner-incident-tolerance"
        hidden={showProjection}
        className="mt-1 text-sm text-slate-700"
      >
        {/* ROUND 13 G7, ninth round -- this strip wrote its own two
            words for signed and unsigned, which were a third and a
            fourth wording for one state. The STATE comes from
            `toleranceSentence` now, the one function every surface
            calls; the breach time is what this strip adds, and it is
            an answer only where something is in force to breach. */}
        {state?.status === "pending"
          ? t("modules.twin.finding.owner.tolerancePending", {
              hours: state.proposed_hours,
            })
          : hours === null
            ? t("modules.twin.finding.owner.toleranceNone")
            : `${asSentence(toleranceSentence(t, state, seats.data))} ${t(
                "modules.twin.finding.owner.breachAt",
                {
                  breach: formatTime(breach?.toISOString() ?? null, i18n.language),
                },
              )}`.trim()}
      </p>
      {/* E4 -- who is on it. The Finding shape carries no assignee by
          design (P2 kept it lean; the workflow lives on the CAP that
          answers it), so the honest answer is the remediating CAP and
          the seat holding it. Where nothing has been raised, saying so
          is the answer -- an owner reading "nobody has picked this up"
          knows what to do next; a blank does not.

          The list 403s for a twin-only viewer, which is not the same
          as "nobody is on it", so that case says nothing rather than
          guessing. */}
      {caps.isSuccess && (
        <p data-testid="owner-incident-who" className="mt-1 text-sm text-slate-700">
          {caps.data.length > 0
            ? t("modules.twin.finding.owner.beingHandled", {
                title: caps.data[0]!.cap_title,
                role: roleLabel(caps.data[0]!.current_position),
              })
            : t("modules.twin.finding.owner.nobodyOnIt")}
        </p>
      )}

      {commentSlot && (
        <div data-testid="owner-incident-comment" className="mt-3">
          {commentSlot}
        </div>
      )}

      {/* What he can do about it, rather than leaving him on a page of
          somebody else's controls. */}
      <p className="mt-2 text-sm">
        <Link
          to={`/twin/ibs/${owned.id}`}
          data-testid="owner-incident-open-ibs"
          className="text-brand-brassDark hover:underline"
        >
          {t("modules.twin.finding.owner.openIbs", { name: owned.name })}
        </Link>
      </p>
      {/* B6 -- fourth round, and the part that kept being missed:
          nothing was ASKED of him. The page told him what had
          happened, who was on it and what it would breach, and then
          stopped -- so an owner who wanted to act had to work out for
          himself that the thing he can do is raise the plan.

          One ask, and only where nothing has been raised: an owner
          looking at a CAP that already exists is being told the
          answer, not asked for one. */}
      {caps.isSuccess && caps.data.length === 0 && (
        <p data-testid="owner-incident-ask" className="mt-2 text-sm text-slate-800">
          {t("modules.twin.finding.owner.ask")}{" "}
          <Link
            to={`/framework/caps?finding=${finding.id}`}
            data-testid="owner-incident-raise-cap"
            className="font-medium text-brand-brassDark hover:underline"
          >
            {t("modules.twin.finding.owner.askAction")}
          </Link>
        </p>
      )}
    </section>
  );
}
